// biome-ignore-all lint/plugin/no-unlabeled-form-inputs: legacy unlabeled inputs, burn-down tracked in HTUI-392
import { useMemo, useState } from "react";
import {
  Column,
  Row,
  Button,
  AddBoxIcon,
  EmptyState,
  Heading,
  Select,
  Text,
} from "@hightouch/htui";
import { DecisionEngineFlowConfig } from "@hightouch/lib/customer-data/decision-engine/types";

import genericPlaceholder from "src/assets/placeholders/generic.svg";
import { SIZES } from "src/ui/box/container-utils";
import { useInsightsContext } from "src/pages/decision-engines/insights/insights-context";
import { useDecisionEngineContext } from "src/pages/decision-engines/decision-engine-context";
import { LinkButton, useSearchParams } from "src/router";

import { useSendInsightCharts } from "./use-send-insight-charts";
import { ChartList } from "./chart-list";
import { ChartBuilder } from "./chart-builder";
import { BUILDER_PARAM, serializeBuilderParam } from "./chart-builder-utils";
import { useLabelResolver } from "./resolve-labels";
import { ChannelConfigurationCard } from "./channel-configuration-card";
import { SyncDeliveryCard } from "./sync-delivery-card";
import { type ChartConfig, extractMetadataColumns } from "./types";

const LOOKBACK_OPTIONS = [
  { value: 30, label: "Last 30 days" },
  { value: 60, label: "Last 60 days" },
  { value: 90, label: "Last 90 days" },
];

export function SendFidelity() {
  const { agent } = useInsightsContext();
  const { channels, engine } = useDecisionEngineContext();
  const flowConfig = agent.config as unknown as DecisionEngineFlowConfig;
  const [deletingChartId, setDeletingChartId] = useState<string | null>(null);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [, setSearch] = useSearchParams();

  const hasInsightsData = Boolean(flowConfig.send_insight_views);
  const hasCampaignId = engine.channels.some(
    (ch) => (ch as any).send_event?.attribution?.campaign_id_column,
  );

  const channelTypes = useMemo(
    () => [...new Set(channels.map((ch) => ch.type))],
    [channels],
  );

  const metadataColumns = useMemo(() => {
    const views = flowConfig.send_insight_views;
    const allCharts = [
      ...((views?.fidelity_charts as ChartConfig[] | undefined) ?? []),
      ...((views?.comparison_charts as ChartConfig[] | undefined) ?? []),
    ];
    return extractMetadataColumns(allCharts);
  }, [flowConfig.send_insight_views]);

  const {
    configs,
    chartResultsById,
    isComputing,
    deleteChart,
    saveChart,
    renameChart,
    isSaving,
    latestSendDate,
  } = useSendInsightCharts({
    flowId: agent.id,
    flowConfig,
    tab: "fidelity",
    lookbackDays,
    mergeTreatmentAndUniform: !flowConfig.show_uniform,
  });

  const resolveLabel = useLabelResolver(agent.id);

  const handleDelete = async (chartId: string) => {
    setDeletingChartId(chartId);
    try {
      await deleteChart(chartId);
    } finally {
      setDeletingChartId(null);
    }
  };

  const openBuilder = () => {
    setSearch({ [BUILDER_PARAM]: serializeBuilderParam("fidelity") });
  };

  return (
    <Column gap={6} p={6} maxWidth={SIZES.page} width="100%" mx="auto">
      <Row justify="space-between" align="start">
        <Column gap={1}>
          <Heading size="md">Send success</Heading>
          <Text color="text.secondary">
            Measures how successfully your scheduled recommendations were
            delivered, both against the warehouse sync changelog and against
            send events from your channels. Send event results depend on the
            send event configuration for each channel.
          </Text>
        </Column>
        <Row gap={3} align="center">
          <Select
            width="sm"
            options={LOOKBACK_OPTIONS}
            value={lookbackDays}
            onChange={(val) => {
              if (val) setLookbackDays(val);
            }}
          />
          {hasInsightsData && (
            <Button icon={AddBoxIcon} onClick={openBuilder}>
              Add chart
            </Button>
          )}
        </Row>
      </Row>

      {hasInsightsData ? (
        <>
          <ChannelConfigurationCard channels={engine.channels} />

          <ChartList
            configs={configs}
            chartResultsById={chartResultsById}
            isComputing={isComputing}
            onDelete={handleDelete}
            onRename={renameChart}
            deletingChartId={deletingChartId}
            resolveLabel={resolveLabel}
            hasCampaignId={hasCampaignId}
            flowName={agent.name}
          />

          <ChartBuilder
            onSave={saveChart}
            isSaving={isSaving}
            metadataColumns={metadataColumns}
            tab="fidelity"
            channelTypes={channelTypes}
            flowId={agent.id}
            resolveLabel={resolveLabel}
          />
        </>
      ) : (
        <EmptyState
          imageUrl={genericPlaceholder}
          title="No send event insights"
          message="Send event insights require send events to be linked to your channels. Configure send events on your channels to start tracking send success."
          actions={
            <LinkButton variant="primary" href="/ai/configuration/channels">
              Configure channels
            </LinkButton>
          }
        />
      )}

      <SyncDeliveryCard
        flowId={agent.id}
        lookbackDays={lookbackDays}
        anchorDate={latestSendDate}
        resolveLabel={resolveLabel}
      />
    </Column>
  );
}
